import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  User, 
  Building, 
  MessageSquare,
  Globe,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { fadeInUp, fadeInLeft, fadeInRight, staggerContainer } from '../utils/cn';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    division: '',
    country: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const offices = [
    {
      country: 'United States',
      city: 'Houston, Texas',
      address: '123 Energy Corridor, Houston',
      phone: '+1 713 123 4567',
      email: 'houston@globaltechnology.com',
      hours: 'Monday - Friday: 8:00 AM - 5:00 PM',
      timezone: 'GMT-6',
      flag: '🇺🇸',
      isHeadquarters: false
    },
    {
      country: 'United Arab Emirates',
      city: 'Dubai',
      address: 'P.O. Box 123456, Dubai, UAE',
      phone: '+971 4 123 4567',
      email: 'dubai@globaltechnology.com',
      hours: 'Sunday - Thursday: 8:00 AM - 6:00 PM',
      timezone: 'GMT+4',
      flag: '🇦🇪',
      isHeadquarters: true
    },
    {
      country: 'Saudi Arabia',
      city: 'Riyadh',
      address: 'King Fahd Road, Riyadh 12345, KSA',
      phone: '+966 11 123 4567',
      email: 'riyadh@globaltechnology.com',
      hours: 'Sunday - Thursday: 8:00 AM - 5:00 PM',
      timezone: 'GMT+3',
      flag: '🇸🇦',
      isHeadquarters: false
    }
  ];

  const divisions = [
    { value: 'telecommunication', label: 'Telecommunication Division' },
    { value: 'power', label: 'Power Division' },
    { value: 'hazardous', label: 'Hazardous Area Products' },
    { value: 'commercial', label: 'Commercial Division' },
    { value: 'general', label: 'General Inquiry' }
  ];

  const subjects = [
    { value: 'quote', label: 'Request Quote' },
    { value: 'support', label: 'Technical Support' },
    { value: 'partnership', label: 'Partnership Opportunity' },
    { value: 'careers', label: 'Career Inquiry' },
    { value: 'general', label: 'General Information' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: '',
        message: '',
        division: '',
        country: ''
      });
    }, 3000);
  };

  const quickContacts = [
    {
      icon: Phone,
      title: 'Call Us',
      description: 'Speak directly with our experts',
      action: 'tel:+97141234567',
      label: '+971 4 123 4567'
    },
    {
      icon: Mail,
      title: 'Email Us',
      description: 'Send us your detailed requirements',
      action: 'mailto:info@globaltechnology.com',
      label: 'info@globaltechnology.com'
    },
    {
      icon: MessageSquare,
      title: 'Live Chat',
      description: 'Chat with our support team',
      action: '#',
      label: 'Start Chat'
    }
  ];

  return (
    <div className="min-h-screen pt-10">
      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden text-white h-[80vh] flex items-center">
        {/* Enhanced Background with Gradient */}
        <div className="absolute inset-0 bg-global-green"></div>
        
        {/* Geometric Pattern Overlay */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `
              repeating-linear-gradient(
                45deg,
                transparent,
                transparent 20px,
                rgba(255,255,255,0.3) 20px,
                rgba(255,255,255,0.3) 22px,
                transparent 22px,
                transparent 40px,
                rgba(255,255,255,0.2) 40px,
                rgba(255,255,255,0.2) 42px
              ),
              repeating-linear-gradient(
                -45deg,
                transparent,
                transparent 30px,
                rgba(255,255,255,0.2) 30px,
                rgba(255,255,255,0.2) 32px,
                transparent 32px,
                transparent 60px,
                rgba(255,255,255,0.1) 60px,
                rgba(255,255,255,0.1) 62px
              )
            `,
            backgroundSize: '80px 80px, 100px 100px'
          }}
        ></div>

        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/20 rounded-full"
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + (i % 4) * 20}%`,
                animation: `float ${4 + i * 0.3}s ease-in-out infinite ${i * 0.4}s`
              }}
            ></div>
          ))}
        </div>
        
        <div className="container-max relative z-8">
          <motion.div
            className="text-center max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            
            {/* Enhanced Lottie Animation Container */}
            <motion.div 
              className="mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <motion.div
                className="relative flex justify-center items-center"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                {/* Glow Effect Behind Animation */}
                <div className="absolute inset-0 bg-white/10 rounded-full blur-3xl scale-75"></div>
                
                <DotLottieReact
                  src="/Contact_Us.lottie"
                  loop
                  autoplay
                  className="relative z-6 w-full max-w-[500px] h-auto max-h-[300px] sm:max-w-[600px] sm:max-h-[350px] lg:max-w-[700px] lg:max-h-[400px] mx-auto"
                  style={{
                    filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.2))'
                  }}
                />
              </motion.div>
            </motion.div>
            
            <motion.p 
              className="text-xl md:text-2xl lg:text-3xl mb-10 leading-relaxed text-white/90 max-w-4xl mx-auto font-light"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
            >
              Let's connect and discuss how we can help your business grow with innovative technology solutions.
            </motion.p>

            {/* Call-to-Action Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.9 }}
            >
              <motion.a
                href="#contact-form"
                className="px-6 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-white/90 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Start Conversation
              </motion.a>
              <motion.a
                href="tel:+97141234567"
                className="px-8 py-4 border-2 border-white/60 text-white hover:bg-white/10 hover:border-white font-semibold rounded-lg transition-all duration-300 "
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Call Now
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Contact Options */}
      <section className="section-padding bg-white relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-accent/5 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-teal-400/3 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Get in Touch Instantly
            </h2>
            <p className="text-xl text-slate max-w-2xl mx-auto">
              Choose your preferred way to connect with our expert team
            </p>
          </motion.div>
          
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8 relative z-10"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {quickContacts.map((contact, index) => {
              const Icon = contact.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="text-center p-8 hover:shadow-2xl transition-all duration-500 bg-white/80 backdrop-blur-sm border-0 shadow-lg group relative overflow-hidden">
                    {/* Hover gradient overlay */}
                    <div className="absolute inset-0 bg-global-green/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Animated border */}
                    <div className="absolute inset-0 rounded-lg bg-global-green/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 p-[1px]">
                      <div className="w-full h-full bg-white rounded-lg"></div>
                    </div>
                    
                    <CardHeader className="relative z-10">
                      <motion.div 
                        className="w-20 h-20 bg-global-green rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-all duration-300"
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Icon className="w-10 h-10 text-white" />
                      </motion.div>
                      <CardTitle className="text-2xl mb-3 group-hover:text-primary transition-colors duration-300">{contact.title}</CardTitle>
                      <CardDescription className="text-lg text-slate/80 group-hover:text-slate transition-colors duration-300">{contact.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="relative z-10">
                      <motion.a 
                        href={contact.action}
                        className="inline-flex items-center text-primary hover:text-accent font-semibold text-lg group-hover:scale-105 transition-all duration-300 px-4 py-2 rounded-lg hover:bg-primary/5"
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        {contact.label}
                        <motion.div
                          animate={{ x: [0, 5, 0] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        >
                          <ArrowRight className={`w-5 h-5 ${isRTL ? 'mr-3 rotate-180' : 'ml-3'}`} />
                        </motion.div>
                      </motion.a>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Office Locations Section */}
      <section id="office-locations" className="section-padding relative overflow-hidden bg-white">
        {/* Enhanced Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-32 right-20 w-36 h-36 bg-accent/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-32 left-20 w-44 h-44 bg-primary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '3s' }}></div>
          <div className="absolute top-1/3 left-1/3 w-28 h-28 bg-teal-400/5 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '6s' }}></div>
        </div>
        
        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <span className="px-6 py-3 bg-global-green/10 text-global-green rounded-full text-sm font-medium border border-global-green/20 shadow-lg backdrop-blur-sm">
                Global Presence
              </span>
            </motion.div>
            <motion.h2 
              className="text-4xl md:text-5xl font-bold text-charcoal mb-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              Our Global Offices
            </motion.h2>
            <motion.p 
              className="text-xl text-slate max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Visit us at any of our offices or contact us directly for immediate assistance and personalized service.
            </motion.p>
          </motion.div>
          
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {offices.map((office, index) => {
              // Find headquarters index for centering
              const headquartersIndex = offices.findIndex(o => o.isHeadquarters);
              const isHeadquarters = office.isHeadquarters;
              
              return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ 
                  duration: 0.6, 
                  delay: index * 0.15,
                  type: "spring",
                  stiffness: 100
                }}
                viewport={{ once: true }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group"
              >
                  <Card className={`shadow-xl border-0 bg-white/90 backdrop-blur-sm relative overflow-hidden transition-all duration-500 group-hover:shadow-2xl ${office.isHeadquarters ? 'p-10 ring-2 ring-primary/30 bg-gradient-to-br from-primary/5 via-white to-accent/5 min-h-[400px]' : 'p-8 min-h-[320px]'}`}>
                    
                    
                    {/* Animated background gradient on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    <CardHeader className="px-0 pt-0 pb-6 relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <CardTitle className="text-xl font-bold flex items-center group-hover:text-primary transition-colors duration-300">
                          <motion.span 
                            className="text-3xl mr-4 inline-block"
                            whileHover={{ scale: 1.2, rotate: 10 }}
                            transition={{ duration: 0.3 }}
                          >
                            {office.flag}
                          </motion.span>
                          <div>
                            <div className="text-xl font-bold text-charcoal group-hover:text-primary transition-colors duration-300">
                              {office.country}
                            </div>
                            <div className="text-lg font-semibold text-primary/80 group-hover:text-primary transition-colors duration-300">
                              {office.city}
                            </div>
                          </div>
                        </CardTitle>
                        {office.isHeadquarters && (
                          <motion.span 
                            className="text-xs bg-gradient-to-r from-primary to-accent text-white px-3 py-1.5 rounded-full font-medium shadow-lg"
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                            viewport={{ once: true }}
                            whileHover={{ scale: 1.1 }}
                          >
                             Headquarters
                          </motion.span>
                        )}
                      </div>
                    </CardHeader>
                    
                    <CardContent className="px-0 space-y-4 relative z-10">
                      <motion.div 
                        className="flex items-start group/item hover:bg-primary/5 p-3 rounded-lg transition-colors duration-300"
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.2, rotate: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <MapPin className="w-5 h-5 text-slate/60 group-hover/item:text-primary mt-0.5 mr-4 flex-shrink-0 transition-colors duration-300" />
                        </motion.div>
                        <span className="text-slate/80 group-hover/item:text-slate transition-colors duration-300 leading-relaxed">
                          {office.address}
                        </span>
                      </motion.div>
                      
                      <motion.div 
                        className="flex items-center group/item hover:bg-primary/5 p-3 rounded-lg transition-colors duration-300"
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.2, rotate: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Phone className="w-5 h-5 text-slate/60 group-hover/item:text-primary mr-4 flex-shrink-0 transition-colors duration-300" />
                        </motion.div>
                        <motion.a 
                          href={`tel:${office.phone}`} 
                          className="text-primary hover:text-accent font-medium transition-colors duration-300 hover:underline"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.2 }}
                        >
                          {office.phone}
                        </motion.a>
                      </motion.div>
                      
                      <motion.div 
                        className="flex items-center group/item hover:bg-primary/5 p-3 rounded-lg transition-colors duration-300"
                        whileHover={{ x: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <motion.div
                          whileHover={{ scale: 1.2, rotate: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <Mail className="w-5 h-5 text-slate/60 group-hover/item:text-primary mr-4 flex-shrink-0 transition-colors duration-300" />
                        </motion.div>
                        <motion.a 
                          href={`mailto:${office.email}`} 
                          className="text-primary hover:text-accent font-medium transition-colors duration-300 hover:underline"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.2 }}
                        >
                          {office.email}
                        </motion.a>
                      </motion.div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
            </motion.div>
          </div>
      </section>

      {/* Global Map Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 via-white to-slate-50 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-40 h-40 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-48 h-48 bg-accent/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-teal-400/3 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
        </div>
        
        <div className="container-max relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="inline-block mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <span className="px-6 py-3 bg-global-green/10 text-global-green rounded-full text-sm font-medium border border-global-green/20 shadow-lg backdrop-blur-sm">
                <Globe className="w-4 h-4 inline-block mr-2" />
                Worldwide Reach
              </span>
            </motion.div>
            <motion.h2 
              className="text-4xl md:text-5xl font-bold text-charcoal mb-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              Find Us Around the Globe
            </motion.h2>
            <motion.p 
              className="text-xl text-slate max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
            >
              Our strategic locations across three continents ensure we're always close to our clients, providing local expertise with global standards.
            </motion.p>
          </motion.div>

          {/* Map Container */}
          <motion.div
            className="relative max-w-6xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {/* Map Image with Enhanced Styling */}
            <motion.div 
              className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200/50 backdrop-blur-sm"
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              {/* Gradient overlay for better integration */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 z-10"></div>
              
              {/* Animated border glow */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 opacity-0 hover:opacity-100 transition-opacity duration-500 blur-sm -z-10"></div>
              
              <motion.img
                src="/Map.png"
                alt="Global Technology Company Worldwide Locations"
                className="w-full h-auto relative z-20"
                style={{
                  filter: 'contrast(1.1) saturate(1.1) brightness(1.05)'
                }}
                initial={{ scale: 1.1 }}
                whileInView={{ scale: 1 }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                viewport={{ once: true }}
              />
              
              {/* Interactive location markers overlay */}
              <div className="absolute inset-0 z-30">
                {/* USA Marker */}
                <motion.div
                  className="absolute"
                  style={{ left: '19.2%', top: '45.2%' }}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.2 }}
                >
                  <div className="relative group cursor-pointer">
                    <motion.div
                      className="w-6 h-6 bg-global-green rounded-full shadow-lg border-2 border-white"
                      animate={{ 
                        boxShadow: [
                          '0 0 0 0 rgba(34, 197, 94, 0.7)',
                          '0 0 0 10px rgba(34, 197, 94, 0)',
                          '0 0 0 0 rgba(34, 197, 94, 0)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    ></motion.div>
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-charcoal text-black px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-xl">
                      Houston, USA
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-charcoal"></div>
                    </div>
                  </div>
                </motion.div>

                {/* UAE Marker */}
                <motion.div
                  className="absolute"
                  style={{ left: '60.3%', top: '48.9%' }}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.2 }}
                >
                  <div className="relative group cursor-pointer">
                    <motion.div
                      className="w-6 h-6 bg-accent rounded-full shadow-lg border-2 border-white"
                      animate={{ 
                        boxShadow: [
                          '0 0 0 0 rgba(251, 146, 60, 0.7)',
                          '0 0 0 12px rgba(251, 146, 60, 0)',
                          '0 0 0 0 rgba(251, 146, 60, 0)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                    ></motion.div>
                    <div className="text-bold absolute bottom-7 left-1/2 transform -translate-x-1/2 bg-charcoal text-black px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-xl">
                      Dubai, UAE (HQ)
                     
                    </div>
                  </div>
                </motion.div>

                {/* Saudi Arabia Marker */}
                <motion.div
                  className="absolute"
                  style={{ left: '62.8%', top: '51.3%' }}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.2 }}
                >
                  <div className="relative group cursor-pointer">
                    <motion.div
                      className="w-6 h-6 bg-primary rounded-full shadow-lg border-2 border-white"
                      animate={{ 
                        boxShadow: [
                          '0 0 0 0 rgba(20, 184, 166, 0.7)',
                          '0 0 0 10px rgba(20, 184, 166, 0)',
                          '0 0 0 0 rgba(20, 184, 166, 0)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity, delay: 1 }}
                    ></motion.div>
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-bold text-black px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-xl">
                      Riyadh, KSA
                      
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-12 lg:py-16 relative overflow-hidden">        
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-5 left-10 w-20 h-20 bg-teal-400/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-24 h-24 bg-accent/20 rounded-full blur-xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
        </div>
        
        <div className="container-max relative z-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center min-h-[400px]">
            {/* Left Side - Polygon Design Pattern */}
            <div className="relative h-full lg:col-span-2">
              {/* Polygon Shape with Hero-style Design */}
              <div 
                className="absolute inset-0 bg-global-green z-5 w-full lg:w-[1000px] h-full"
                style={{
                  clipPath: 'polygon(0 0, 100% 0, 65% 100%, 0% 100%)'
                }}
              >
                {/* Clean Geometric Pattern */}
                <div 
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        45deg,
                        transparent,
                        transparent 20px,
                        rgba(255,255,255,0.3) 20px,
                        rgba(255,255,255,0.3) 22px,
                        transparent 22px,
                        transparent 40px,
                        rgba(255,255,255,0.2) 40px,
                        rgba(255,255,255,0.2) 42px
                      ),
                      repeating-linear-gradient(
                        -45deg,
                        transparent,
                        transparent 30px,
                        rgba(255,255,255,0.2) 30px,
                        rgba(255,255,255,0.2) 32px,
                        transparent 32px,
                        transparent 60px,
                        rgba(255,255,255,0.1) 60px,
                        rgba(255,255,255,0.1) 62px
                      )
                    `,
                    backgroundSize: '80px 80px, 100px 100px'
                  }}
                />
                {/* Additional Geometric Lines */}
                <div 
                  className="absolute inset-0 opacity-8"
                  style={{
                    backgroundImage: `
                      repeating-linear-gradient(
                        0deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      ),
                      repeating-linear-gradient(
                        90deg,
                        transparent,
                        transparent 50px,
                        rgba(255,255,255,0.15) 50px,
                        rgba(255,255,255,0.15) 51px
                      )
                    `
                  }}
                />
              </div>
              
              {/* Content over polygon */}
              <motion.div
                className="relative z-20 text-white p-4 sm:p-6 lg:p-6 h-full flex flex-col justify-center max-w-[75%] sm:max-w-[70%] lg:max-w-[60%]"
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >      
                <motion.h2 
                  className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  viewport={{ once: true }}
                >
                  Get in Touch with Our Team
                </motion.h2>
                
                <motion.p 
                  className="text-xs sm:text-sm lg:text-base mb-3 sm:mb-4 text-gray-100 leading-relaxed"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  viewport={{ once: true }}
                >
                  Ready to discuss your project? We're here to help you find the perfect solution for your business needs.
                </motion.p>
                
                <CardContent className="px-0 relative z-10">
                  {isSubmitted ? (
                    <motion.div
                      className="text-center py-12"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                      >
                        <CheckCircle className="w-20 h-20 text-primary mx-auto mb-6" />
                      </motion.div>
                      <motion.h3 
                        className="text-2xl font-bold text-primary mb-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        Message Sent Successfully!
                      </motion.h3>
                      <motion.p 
                        className="text-slate text-lg"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                      >
                        Thank you for contacting us. We'll respond within 24 hours.
                      </motion.p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                      <motion.div 
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true }}
                      >
                        <div className="group">
                          <label className="block text-xs font-semibold text-charcoal mb-2 group-focus-within:text-primary transition-colors duration-300">
                            Full Name *
                          </label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate/50 group-focus-within:text-primary transition-colors duration-300" />
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              className="w-full pl-5 pr-3 py-3 border-2 border-soft-gray rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white/50 backdrop-blur-sm hover:bg-white/80 text-sm"
                              placeholder="Your full name"
                            />
                          </div>
                        </div>
                        <div className="group">
                          <label className="block text-xs font-semibold text-charcoal mb-2 group-focus-within:text-primary transition-colors duration-300">
                            Email Address *
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate/50 group-focus-within:text-primary transition-colors duration-300" />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              className="w-full pl-5 pr-3 py-3 border-2 border-soft-gray rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white/50 backdrop-blur-sm hover:bg-white/80 text-sm"
                              placeholder="your.email@company.com"
                            />
                          </div>
                        </div>
                      </motion.div>
                      
                      <motion.div 
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                      >
                        <div className="group">
                          <label className="block text-xs font-semibold text-charcoal mb-2 group-focus-within:text-primary transition-colors duration-300">
                            Company
                          </label>
                          <div className="relative">
                            <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate/50 group-focus-within:text-primary transition-colors duration-300" />
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleInputChange}
                              className="w-full pl-5 pr-3 py-3 border-2 border-soft-gray rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white/50 backdrop-blur-sm hover:bg-white/80 text-sm"
                              placeholder="Your company name"
                            />
                          </div>
                        </div>
                        <div className="group">
                          <label className="block text-xs font-semibold text-charcoal mb-2 group-focus-within:text-primary transition-colors duration-300">
                            Phone Number
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate/50 group-focus-within:text-primary transition-colors duration-300" />
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleInputChange}
                              className="w-full pl-5 pr-3 py-3 border-2 border-soft-gray rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-white/50 backdrop-blur-sm hover:bg-white/80 text-sm"
                              placeholder="+971 50 123 4567"
                            />
                          </div>
                        </div>
                      </motion.div>
                      
                      <motion.div 
                        className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        viewport={{ once: true }}
                      >
                        <div className="group">
                          <label className="block text-xs font-semibold text-black mb-2 group-focus-within:text-primary transition-colors duration-300">
                            Division of Interest
                          </label>
                          <select
                            name="division"
                            value={formData.division}
                            onChange={handleInputChange}
                            className="w-full px-3 py-3 border-2 border-soft-gray rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-black backdrop-blur-sm  text-sm"
                          >
                            <option value="">Select Division</option>
                            {divisions.map((division) => (
                              <option key={division.value} value={division.value}>
                                {division.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="group">
                          <label className="block text-xs font-semibold text-charcoal mb-2 group-focus-within:text-primary transition-colors duration-300">
                            Subject *
                          </label>
                          <select
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            required
                            className="w-full px-3 py-3 border-2 border-soft-gray rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-black backdrop-blur-sm  text-sm"
                          >
                            <option value="">Select Subject</option>
                            {subjects.map((subject) => (
                              <option key={subject.value} value={subject.value}>
                                {subject.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </motion.div>
                      
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        viewport={{ once: true }}
                        className="group"
                      >
                        <label className="block text-xs font-semibold text-charcoal mb-2 group-focus-within:text-primary transition-colors duration-300">
                          Message *
                        </label>
                        <div className="relative">
                          <MessageSquare className="absolute left-3 top-4 w-4 h-4 text-slate/50 group-focus-within:text-primary transition-colors duration-300" />
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required
                            rows={4}
                            className="w-full pl-10 pr-3 py-3 border-2 border-soft-gray rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 resize-none bg-white/50 backdrop-blur-sm hover:bg-white/80 text-sm"
                            placeholder="Please describe your requirements or inquiry in detail..."
                          />
                        </div>
                      </motion.div>
                      
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        viewport={{ once: true }}
                      >
                        <Button
                          type="submit"
                          size="sm"
                          className="w-full py-3 text-sm font-semibold bg-global-green hover:bg-global-green/90 transform hover:scale-[1.02] transition-all duration-300 shadow-lg hover:shadow-xl"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <motion.div 
                              className="flex items-center justify-center"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3" />
                              Sending Message...
                            </motion.div>
                          ) : (
                            <motion.div 
                              className="flex items-center justify-center"
                              whileHover={{ x: 5 }}
                              transition={{ duration: 0.2 }}
                            >
                              Send Message
                              <Send className={`w-5 h-5 ${isRTL ? 'mr-3 rotate-180' : 'ml-3'}`} />
                            </motion.div>
                          )}
                        </Button>
                      </motion.div>
                    </form>
                  )}
                </CardContent>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;